import React, { useEffect, useReducer } from 'react';
import { useState } from 'react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { getRedirectUriQueryParam } from '../../../../utils/url';
import { SectionData } from '../../../survey_engine/SurveyTemplates/Section';
import { SectionList } from '../../../survey_engine/SurveyTemplates/SectionList';
import {
  Action,
  initSections,
  reducer,
} from '../../../survey_engine/SurveyTemplates/dispatcher';
import { InterviewKitTemplateService } from '../../../../services/v1/applicant_tracking/InterviewKitTemplateService';
import { FIELD_CONFIGS, areSectionsSavable, fetchTemplate } from '../data';
import { Card } from '../../../../components/Card';
import { CardHeader } from '../../../survey_engine/SurveyTemplates/CardHeader';
import { PageHeader } from '../../../survey_engine/SurveyTemplates/PageHeader';
import { NameInput } from '../../../survey_engine/SurveyTemplates/NameInput';
import { StartingPoint } from '../../../survey_engine/SurveyTemplates/StartingPointSelector';
import { InterviewKitTemplateStartingPointSelector } from '../InterviewKitTemplateStartingPointSelector';
import { DefaultAlert } from '../../../../components/DefaultAlert';
import { AlertObject, AlertType } from '../../../../components/Alert';

const REDIRECT_URI =
  getRedirectUriQueryParam() ?? '/organization_settings/interview_questions';

export default function NewInterviewKitTemplatePage() {
  // State
  const [name, setName] = useState<string>('');
  const [startingPoint, setStartingPoint] = useState<StartingPoint>(null);
  const [sections, dispatch] = useReducer(reducer, null, () =>
    initSections('text'),
  );
  const [alert, setAlert] = useState<AlertObject>(null);
  const [error, setError] = useState<string>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Derived State
  function isSavable() {
    if (!name.trim()) return false;
    if (startingPoint === undefined) return false;
    if (!areSectionsSavable(sections)) return false;

    return true;
  }

  // Handlers
  async function handleStartingPointChange(startingPoint: StartingPoint) {
    setStartingPoint(startingPoint);

    if (startingPoint == null) return;

    if (typeof startingPoint === 'string' && startingPoint === 'FROM_SCRATCH') {
      dispatch({ type: 'reset', newState: initSections('text') });
      return;
    }

    setIsLoading(true);

    const template = await fetchTemplate(startingPoint.value);
    dispatch({ type: 'reset', newState: template.sections });

    setIsLoading(false);
  }

  async function handleSave() {
    setIsLoading(true);

    try {
      const formattedSections = sections.map(({ name, fields }) => ({
        name,
        questions: fields.map(({ title, fieldType, max, aiFillEnabled }) => ({
          title,
          fieldType,
          max,
          aiFillEnabled,
        })),
      }));

      await InterviewKitTemplateService.create({
        name,
        sections: formattedSections,
      });
      window.location.href = REDIRECT_URI;
    } catch ({ status, body }) {
      if (status === 409) {
        const message = body?.message;
        setAlert({ type: AlertType.Danger, message });
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (error) setError(null);
  }, [name]);

  return (
    <>
      {isLoading && <LoadingSpinner showBackdrop />}
      <DefaultAlert alert={alert} setAlert={setAlert} />
      <Body
        isSavable={isSavable()}
        name={name}
        error={error}
        onName={setName}
        startingPoint={startingPoint}
        onStartingPointChange={handleStartingPointChange}
        sections={sections}
        dispatch={dispatch}
        onSave={handleSave}
      />
    </>
  );
}

function Body(props: {
  isSavable: boolean;
  name: string;
  error: string;
  onName: (value: string) => void;
  startingPoint: StartingPoint;
  onStartingPointChange: (value: StartingPoint) => void;
  sections: SectionData[];
  dispatch: React.Dispatch<Action>;
  onSave: () => void;
}) {
  return (
    <div className='container'>
      <PageHeader title='Add Interview Question' backUrl={REDIRECT_URI} />
      <Card>
        <CardHeader
          title='Interview Questions'
          isSavable={props.isSavable}
          onSaveClick={props.onSave}
          cancelUrl={REDIRECT_URI}
        />
        <div className='border-bottom'>
          <div className='d-flex flex-column w-50 fs-5 pt-2 pb-4 gap-4'>
            <NameInput
              name={props.name}
              onName={props.onName}
              error={props.error}
              placeholder='Insert Name'
            />
            <InterviewKitTemplateStartingPointSelector
              startingPoint={props.startingPoint}
              onStartingPointChange={props.onStartingPointChange}
            />
          </div>
        </div>
        <SectionList
          fieldConfigs={FIELD_CONFIGS}
          singleTypeSections={true}
          sections={props.sections}
          dispatch={props.dispatch}
          title='Questions'
          subtitle='Define and manage your interview questions.'
          addFieldButtonText='Add Question'
          fieldDeleteTooltip='Remove Question'
          defaultFieldType='text'
          columns={[
            {
              fieldName: 'text',
              className: 'col',
              label: 'Questions',
              helperText: 'Maximum 255 characters',
              placeholder: 'Insert Question',
            },
          ]}
        />
      </Card>
    </div>
  );
}
